import { useLocale } from "../i18n/LocaleProvider";

export type ProvenanceVerdict =
  | "cdn-trusted"
  | "c2pa-verified"
  | "synthid-detected"
  | "first-party"
  | "unverified";

type Tone = "amber" | "purple" | "slate";

const STYLE: Record<Tone, string> = {
  amber: "bg-amber-500/15 border-amber-400/60 text-amber-100",
  purple: "bg-purple-500/15 border-purple-400/60 text-purple-100",
  slate: "bg-slate-700/40 border-slate-500/60 text-slate-200",
};

function classify(verdict: ProvenanceVerdict): {
  key: string;
  tone: Tone;
} {
  switch (verdict) {
    case "cdn-trusted":
      return { key: "provenanceCdnTrusted", tone: "amber" };
    case "c2pa-verified":
      return { key: "provenanceC2pa", tone: "amber" };
    case "synthid-detected":
      return { key: "provenanceSynthId", tone: "amber" };
    case "first-party":
      return { key: "provenanceFirstParty", tone: "purple" };
    case "unverified":
      return { key: "provenanceUnverified", tone: "amber" };
  }
}

interface Props {
  verdict: ProvenanceVerdict | string | null | undefined;
  signer?: string;
}

/**
 * Pill telling the viewer how we know this piece is AI. Surfaced on the
 * piece-detail drawer + the /create confirm step (WEB16 · I15).
 */
export default function ProvenanceBadge(props: Props) {
  const { t } = useLocale();
  const verdict = (props.verdict ?? "unverified") as ProvenanceVerdict;
  // Unknown verdict strings fall through to the "pending" pill rather than
  // breaking the render — protects against an out-of-band schema bump.
  const knownVerdicts: ProvenanceVerdict[] = [
    "cdn-trusted",
    "c2pa-verified",
    "synthid-detected",
    "first-party",
    "unverified",
  ];
  if (!knownVerdicts.includes(verdict)) {
    return (
      <span
        class={`inline-flex w-fit items-center gap-2 px-3 py-1 rounded-full border text-xs font-display ${STYLE.slate}`}
        data-testid="provenance-badge"
        data-verdict="unknown"
      >
        <span class="w-1.5 h-1.5 rounded-full bg-slate-300" />
        {t("provenanceUnknown")}
      </span>
    );
  }

  const { key, tone } = classify(verdict);
  return (
    <span
      class={`inline-flex w-fit items-center gap-2 px-3 py-1 rounded-full border text-xs font-display ${STYLE[tone]}`}
      data-testid="provenance-badge"
      data-verdict={verdict}
      title={props.signer ? `${t(key)} · ${props.signer}` : t(key)}
    >
      <span class={`w-1.5 h-1.5 rounded-full ${tone === "amber" ? "bg-amber-300" : tone === "purple" ? "bg-purple-300" : "bg-slate-300"}`} />
      {t(key)}
      {props.signer ? <span class="opacity-70">· {props.signer}</span> : null}
    </span>
  );
}
